import { useCallback, useEffect, useMemo, useState, type FC } from "react";
import type { FormikHelpers } from "formik";
import { useTranslation } from "react-i18next";
import {
  useAppDispatch,
  useAppSelector,
  useReportCommonStates,
  useReportInactiveObjects
} from "hooks";
import { Table } from "@vilocnv/allsetra-core";
import { Box, useTheme } from "@mui/material";
import ReportsTopbar from "components/common/reports/ReportsTopbar/ReportsTopbar";
import ReportsFilterBar from "components/common/reports/ReportFiltersBar/ReportsFilterBar";
import ReportEmptyState from "components/common/reports/ReportEmptyState";
import ReportsCommonFilterForm from "components/forms/Reports/ReportsCommonFilterForm";
import { ACTIVITY_TABLE_COLUMNS } from "app/data/constants/Reports/activityReportsConstants";
import { selectAccountKeysState } from "app/data/selectors";
import {
  buildActivityReportPayload,
  handleDateRange
} from "app/data/helpers/ReportHelpers/activityReportHelpers";
import {
  generateReportThunk,
  getAllKeysByAccountThunk,
  resetReports,
  resetReportsFilterData,
  setReportsFilterData,
  setReportsDateValues
} from "app/features";
import ActivityExpendableRowCard from "components/cards/ReportCards/ActivityExpendableRowCard.tsx/ActivityExpendableRowCard";
import {
  commonReportsDataFormatter,
  commonReportsFilterFormValidationSchema,
  customStyles,
  getStandardizedReportDates,
  reportsCommonFormInitialValues
} from "app/data/helpers/ReportHelpers/commonReportHelpers";
import { getResetFilterFormValues } from "app/data/helpers/ReportHelpers";

const ActivityReports: FC = () => {
  const theme = useTheme();
  const dispatch = useAppDispatch();

  const {
    accountGroups,
    dateFormat,
    dateValues,
    drawerSelectedAccountId,
    filterOpen,
    handleObjectsSearch,
    handleOnChangeReportFilterForm,
    objectTypes,
    objectTypesLoading,
    objectsLoading,
    report,
    reportsLoading,
    reportsFilterData,
    filterValues,
    setFilterValues,
    mergedObjectOptions,
    getHydratedFormInitialValues,
    setSelectedLocalIds,
    resetReportDates,
    setDateFormat,
    setDateValues,
    setFilterOpen
  } = useReportCommonStates();

  const [openModal, setOpenModal] = useState(false);
  const [apiPayload, setApiPayload] = useState(() =>
    reportsFilterData && Object.keys(reportsFilterData).length
      ? reportsFilterData
      : null
  );
  const { applyInactiveFilter, statusLookupsReady, showInactiveObjects } =
    useReportInactiveObjects();
  const tablePending = reportsLoading || !statusLookupsReady;
  const { t } = useTranslation(["translation", "tableHeadingsTranslation"]);

  const { accountKeys, loading: accountKeysLoading } = useAppSelector(
    selectAccountKeysState
  );

  const formattedData = useMemo(
    () =>
      applyInactiveFilter(
        commonReportsDataFormatter(report, "object", dateFormat)
      ),
    [report, dateFormat, applyInactiveFilter]
  );

  const tableColumns = useMemo(() => ACTIVITY_TABLE_COLUMNS(t), [t]);

  const activityRegistrationFiltersSubmitHandler = async (
    values: any,
    formikHelpers: FormikHelpers<any>
  ) => {
    formikHelpers.setSubmitting(true);
    setFilterValues(values);
    const payload = {
      ...(await buildActivityReportPayload(apiPayload, values, dateValues)),
      object: values.assignedToAllObjects ? [] : values.object
    };
    dispatch(setReportsFilterData(payload));
    setApiPayload(payload);

    formikHelpers.setSubmitting(false);
    setFilterOpen(false);
  };

  const handleDateChange = useCallback((value: any) => {
    const formattedValue = getStandardizedReportDates(value);
    setDateValues(formattedValue);
    dispatch(setReportsDateValues(formattedValue));
  }, []);

  const handleDateRangeSubmit = () => {
    const newPayload = {
      ...handleDateRange(filterValues, dateValues, apiPayload, 6),
      object: filterValues.assignedToAllObjects
        ? []
        : (filterValues.object ?? [])
    };
    setApiPayload(newPayload);
  };

  const handleLoadReport = () => {
    const newPayload = {
      ...handleDateRange(filterValues, dateValues, apiPayload, 6),
      object: filterValues.assignedToAllObjects
        ? []
        : (filterValues.object ?? [])
    };

    dispatch(setReportsFilterData(newPayload));
    dispatch(generateReportThunk(newPayload));
  };

  useEffect(() => {
    // dispatch(resetReports());
    handleDateRangeSubmit();
  }, []);

  useEffect(() => {
    if (drawerSelectedAccountId !== null) {
      dispatch(getAllKeysByAccountThunk(drawerSelectedAccountId));
    }
  }, [drawerSelectedAccountId]);

  return (
    <Box>
      <ReportsTopbar
        dropdownTitle={t("reports.activityReport")}
        disabled={reportsLoading}
      />
      <Box mx={4}>
        <ReportsFilterBar
          setFilterOpen={setFilterOpen}
          dateFormat={dateFormat}
          setDateFormat={setDateFormat}
          handleDateChange={handleDateChange}
          dateValues={dateValues}
          handleDateRangeSubmit={handleDateRangeSubmit}
          dateRangeLoading={reportsLoading}
          handleLoadReport={handleLoadReport}
          disableExportButton={formattedData.length === 0}
          hideDateFormats={true}
          reportType={6}
          reportExportTypeValue={2}
          openModal={openModal}
          setOpenModal={setOpenModal}
          exportModalButton={true}
          hideExportButton={true}
          formattedData={formattedData}
        />
        <ReportsCommonFilterForm
          fetchOnDebounce={handleObjectsSearch}
          open={filterOpen}
          onClose={() => setFilterOpen(false)}
          onSubmit={activityRegistrationFiltersSubmitHandler}
          groups={accountGroups}
          objectTypes={objectTypes}
          objects={mergedObjectOptions}
          keys={accountKeys}
          dataLoading={
            objectsLoading || objectTypesLoading || accountKeysLoading
          }
          //@ts-ignore
          onChange={handleOnChangeReportFilterForm}
          formInitialValues={getHydratedFormInitialValues(
            reportsCommonFormInitialValues
          )}
          formValidations={commonReportsFilterFormValidationSchema}
          onResetFilters={() => {
            dispatch(resetReportsFilterData());
            resetReportDates();
            setSelectedLocalIds([]);
            setApiPayload(null);
            setFilterValues(
              getResetFilterFormValues(reportsCommonFormInitialValues)
            );
          }}
          theme={theme}
        />
        <Table
          columns={tableColumns}
          data={formattedData}
          progressPending={tablePending}
          noDataComponent={!tablePending ? <ReportEmptyState /> : null}
          expandableRows
          expandOnRowClicked
          expandableRowsComponent={ActivityExpendableRowCard}
          pagination={false}
          nestedRowPadding={"16px"}
          headerPadding={"50px"}
          translator={t}
          customStyles={customStyles}
        />
      </Box>
    </Box>
  );
};

export default ActivityReports;
